/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { fundraiserGettingStartedSchema } from "../../../utils/validationSchemas";
import { useFormik } from "formik";
import Input from "../../common/FormInputs";
import Selector from "../../common/Selector";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import GoogleMaps from "../../common/GoogleMaps";
import InputTextArea from "../../common/InputTextArea";
import PrimaryButton from "../../common/PrimaryButton";
import PhoneFormInput from "../../common/PhoneInput";
import {
  createReason,
  getReasons,
  sendFundraiserInvite,
} from "../../../services/onboardingService";
import { emailVerification } from "../../../services/authService";
import msgIcon from "../../../Assets/Icons/envelopIcon.svg";
import {
  getFundraiserCategories,
  getSubCategoriesByCategoryId,
} from "../../../services/fundraiserService";
import Loader from "../../common/Loader";
import AlreadyRegisteredPopup from "../../common/AlreadyRegisteredPopup";
import { useNavigate } from "react-router-dom";
import toast from "../../../utils/toast";
import { uploadFile } from "../../../utils/s3";
import { ReactComponent as CameraIcon } from "../../../Assets/Icons/cameraIcon.svg";

const GettingStarted = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [locationDetails, setLocationDetails] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [subCategoryIds, setSubCategoryIds] = useState([]);
  const [emailSent, setEmailSent] = useState(false);

  const [allReasons, setAllReasons] = useState([]);
  const [customReasonId, setCustomReasonId] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [isOtherCategorySelected, setIsOtherCategorySelected] = useState(false);
  const [customCategory, setCustomCategory] = useState("");
  const [hideSubForCustom, setHideSubForCustom] = useState(false);

  const [logoFileImage, setLogoFileImage] = useState();
  const [logoUrl, setLogoUrl] = useState();
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoPreviewStyle, setLogoPreviewStyle] = useState({});
  const [emailExistsAsUser, setEmailExistsAsUser] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const loading = useSelector((state) => state?.onboardingReducer?.loading);

  const getCategoriesData = async () => {
    const categoryPromise = await dispatch(getFundraiserCategories())?.unwrap();
    if (categoryPromise?.success === true) {
      setCategories(categoryPromise?.data);
    }
  };

  const getSubCategoriesData = async (categoryId) => {
    try {
      const subCategoryPromise = await dispatch(
        getSubCategoriesByCategoryId({ id: categoryId })
      )?.unwrap();
      if (subCategoryPromise?.success === true) {
        setSubCategories(subCategoryPromise?.data);
      }
    } catch (error) {
      console.error("Error fetching subcategories", error);
    }
  };

  const getReasonsData = async () => {
    const reasonsPromise = await dispatch(
      getReasons({ isApproved: true })
    )?.unwrap();
    if (reasonsPromise?.success === true) {
      // Use only global/approved reasons; custom/user-scoped excluded
      const globalApproved = (reasonsPromise?.data || []).filter((r) => !(r?.merchantId || r?.merchant || r?.createdBy));
      setAllReasons(globalApproved);
    }
  };

  // Check if email is registered as User/Member
  const checkEmailExistsAsUser = async (emailValue) => {
    if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setEmailExistsAsUser(false);
      return false;
    }

    setIsCheckingEmail(true);
    try {
      const result = await dispatch(emailVerification({ email: emailValue }))?.unwrap();
      // If email exists (result?.data === true), it means it's registered as User/Member
      if (result?.success === true && result?.data === true) {
        setEmailExistsAsUser(true);
        setIsCheckingEmail(false);
        return true;
      } else {
        setEmailExistsAsUser(false);
        setIsCheckingEmail(false);
        return false;
      }
    } catch (error) {
      // If there's an error, assume email doesn't exist as User/Member
      setEmailExistsAsUser(false);
      setIsCheckingEmail(false);
      return false;
    }
  };

  const baseCategoryOrder = [
    "Sporting Clubs",
    "Schools",
    "Community Organisations",
  ];
  const fundraiserCategoryOptions = [
    ...(
      baseCategoryOrder
        .map((title) => ({
          value: title,
          label: title,
        }))
    ),
    { value: "Other", label: "Other" },
  ];

  useEffect(() => {
    getCategoriesData();
    getReasonsData();
  }, []);

  const checkOptions = allReasons.map((reason) => reason.reason);
  checkOptions.push("Other");

  const initialValues = {
    fundraiserName: "",
    fundraiserAddress: "",
    logo: "",
    websiteUrl: "",
    personName: "",
    personContact: "",
    email: "",
    fundraiserCategory: null,
    fundraiserSubCategory: [],
    aboutOrganization: "",
    reasons: [],
    newReason: null,
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: fundraiserGettingStartedSchema,

    onSubmit: async (values) => {
      // Check if email is registered as User/Member before submission
      const isUserEmail = await checkEmailExistsAsUser(values.email);
      if (isUserEmail) {
        formik.setFieldError("email", "This email is already registered. Please use a different email address or sign in with your existing account.");
        formik.setTouched({ ...formik.touched, email: true }, false);
        scrollToFirstError({ email: "This email is already registered." });
        return;
      }

      // Require custom category submission when Other is chosen
      if (values.fundraiserCategory === "Other") {
        if (!customCategory || customCategory.trim() === "") {
          formik.setFieldError("fundraiserCategory", "Please enter a custom category since you selected Other");
          return;
        }
      }
      // Custom validation for 'Other' reason
      if (values.reasons.includes("Other")) {
        if (!values.newReason || values.newReason.trim() === "") {
          formik.setFieldError("newReason", "Please enter a custom reason since you selected Other");
          return;
        } else if (!customReasonId) {
          // If newReason is filled but not submitted (user didn't click Submit button)
          formik.setFieldError("newReason", "Please click Submit to add your custom reason.");
          return;
        }
      }

      // Prepare reason array
      let reasonArray = [];
      if (Array.isArray(values.reasons) && values.reasons.length > 0) {
        const ids = values.reasons
          .map((name) => allReasons.find((r) => r.reason === name)?._id)
          .filter(Boolean);
        reasonArray = ids.map((id) => ({ _id: id }));
      }
      if (customReasonId) {
        reasonArray.push({ _id: customReasonId });
      }

      const payload = {
        fundraiserName: values?.fundraiserName,
        address: locationDetails?.placeName || values?.fundraiserAddress,
        logo: values?.logo || logoUrl || "",
        website: values?.websiteUrl,
        contactPersonName: values?.personName,
        contactPersonNumber: values?.personContact,
        email: values?.email,
        ...(values?.fundraiserCategory === "Other"
          ? { customCategoryTitle: customCategory.trim() }
          : { category: [categoryId] }
        ),
        subCategory: subCategoryIds,
        description: values?.aboutOrganization,
        reason: reasonArray,
      };

      try {
        const sendPromise = await dispatch(
          sendFundraiserInvite(payload)
        )?.unwrap();
        if (sendPromise?.success === true) {
          setEmailSent(true);
          formik.resetForm();
          setLocationDetails(null);
          setCustomReasonId(null);
          setCategoryId(null);
          setSubCategoryIds([]);
          setSubCategories([]);
          setIsOtherCategorySelected(false);
          setCustomCategory("");
          setEmailExistsAsUser(false);
          setMapKey(prev => prev + 1);

        }
      } catch (error) {
        // Handle backend errors - check if email is already registered
        const errorMessage = error?.response?.data?.message || error?.message || "";
        if (errorMessage.toLowerCase().includes("already exists") ||
          errorMessage.toLowerCase().includes("already registered") ||
          errorMessage.toLowerCase().includes("user") ||
          errorMessage.toLowerCase().includes("member")) {
          formik.setFieldError("email", "This email is already registered. Please use a different email address or sign in with your existing account.");
          formik.setTouched({ ...formik.touched, email: true }, false);
          setEmailExistsAsUser(true);
          scrollToFirstError({ email: "This email is already registered." });
        } else {
          toast.error(errorMessage || "Failed to send invitation. Please try again.");
        }
      }
    },
  });

  const {
    fundraiserName,
    fundraiserAddress,
    websiteUrl,
    personName,
    personContact,
    email,
    fundraiserCategory,
    fundraiserSubCategory,
    aboutOrganization,
    reasons,
    newReason,
  } = formik.values;

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
  } = formik;

  const scrollToFirstError = (formErrors) => {
    console.log("Scroll to first error called with:", formErrors);
    const priority = [
      'fundraiserName',
      'fundraiserAddress',
      'websiteUrl',
      'fundraiserCategory',
      'fundraiserSubCategory',
      'personName',
      'email',
      'personContact',
      'aboutOrganization',
      'reasons',
      'newReason'
    ];
    let targetField = priority.find((key) => formErrors && Object.prototype.hasOwnProperty.call(formErrors, key));
    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }
    console.log("Target field:", targetField);
    if (!targetField) return;
    let el = document.querySelector(`[data-field="${targetField}"]`);
    if (!el) el = document.querySelector(`[name="${targetField}"]`);
    if (!el) el = document.querySelector(`input[name="${targetField}"]`);
    if (!el) el = document.querySelector(`textarea[name="${targetField}"]`);
    if (!el) el = document.querySelector(`select[name="${targetField}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof el.focus === 'function') { try { el.focus(); } catch (_) { } }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      formik.setTouched(Object.keys(validationErrors).reduce((acc, k) => { acc[k] = true; return acc; }, {}), true);
      scrollToFirstError(validationErrors);
      return;
    }
    handleSubmit(e);
  };

  const validateImage = (fileName) => {
    const idxDot = fileName.lastIndexOf(".") + 1;
    const extFile = fileName.substr(idxDot, fileName.length).toLowerCase();
    return ["jpg", "jpeg", "png", "svg"].includes(extFile);
  };

  const handleUploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fileName = file.name;
    setLogoPreviewStyle({});
    setLogoLoading(true);
    try {
      const s3Url = await uploadFile(file, fileName);
      if (s3Url) {
        setLogoUrl(s3Url);
        // Persist uploaded key/url into form state for submission
        setFieldValue("logo", s3Url);
      }
    } finally {
      setLogoLoading(false);
    }
    if (validateImage(fileName)) {
      const logoImageUrl = URL.createObjectURL(file);
      setLogoFileImage(logoImageUrl);
      setLogoPreviewStyle({});
    } else {
      toast.error("Only jpg/jpeg, png, and svg files are allowed!");
    }
  };

  const handleClearLogo = () => {
    setFieldValue("logo", "");
    setLogoFileImage(undefined);
    setLogoPreviewStyle({});
  };

  const subCategoryOptions = subCategories
    ?.filter((subCat) => {
      const selectedSubCategories = formik.values.fundraiserSubCategory || [];
      return !selectedSubCategories.includes(subCat?.title);
    })
    ?.map((subCat) => ({
      value: subCat?.title ?? "",
      label: subCat?.title ?? "",
    }));

  const handleClosePopup = () => {
    setEmailSent(false);
  };

  const handleLocationSelect = (selectedLocation) => {
    setFieldValue("fundraiserAddress", selectedLocation?.placeName);
    setLocationDetails(selectedLocation);
  };

  const handleCategoryChange = async (newCategory) => {
    setHideSubForCustom(false);
    setFieldValue("fundraiserCategory", newCategory);
    setFieldValue("fundraiserSubCategory", []);
    setSubCategories([]);
    setIsOtherCategorySelected(newCategory === "Other");
    if (newCategory === "Other") {
      setCategoryId(null);
      return;
    }
    if (newCategory) {
      const categoryToFetch = categories?.find(
        (cat) => cat?.title === newCategory
      );
      if (categoryToFetch) {
        await getSubCategoriesData(categoryToFetch._id);
      }
    }
  };

  const handleSubCategoryDeselect = (removedSubCategory) => {
    const updatedSubCats = fundraiserSubCategory.filter(
      (subCat) => subCat !== removedSubCategory
    );
    setFieldValue("fundraiserSubCategory", updatedSubCats);
  };

  useEffect(() => {
    if (fundraiserCategory && categories?.length !== 0) {
      if (fundraiserCategory === "Other") {
        setCategoryId(null);
      } else {
        const selectedCategory = categories?.find(
          (cat) => cat?.title === fundraiserCategory
        );
        setCategoryId(selectedCategory?._id || null);
      }
    } else {
      setCategoryId(null);
    }
  }, [fundraiserCategory, categories]);

  useEffect(() => {
    if (fundraiserSubCategory?.length !== 0 && subCategories?.length !== 0) {
      const subCatIds = fundraiserSubCategory?.map(
        (fundraiserSubCategoryName) => {
          const matchingSubCategories = subCategories?.filter(
            (subCat) => subCat?.title === fundraiserSubCategoryName
          );
          return matchingSubCategories?.map((subCat) => subCat?._id);
        }
      );
      setSubCategoryIds(subCatIds?.flat());
    }
  }, [fundraiserSubCategory, subCategories]);

  return (
    <>
      {loading && <Loader />}
      <form
        className="w-full min-h-screen py-6 sm:py-8 md:py-10 px-4 sm:px-6 md:px-0 flex flex-col items-center offerform"
        onSubmit={handleFormSubmit}
      >
        {emailSent === true && (
          <AlreadyRegisteredPopup
            popupClassName={"w-[335px] sm:w-[500px] pb-5"}
            content={
              <div className="flex flex-col items-center p-6">
                <img src={msgIcon} className="w-[50px] h-[50px] mb-4" alt="Email Sent" />
                <h3 className="text-[18px] font-[600] text-[#303030] mb-2 font-poppins">
                  Invitation Sent!
                </h3>
                <p className="text-[14px] text-[#767676] text-center mb-6 font-poppins">
                  We have sent an invitation to your email to join Swoodle. Please check your inbox
                  (and spam folder) to continue.
                </p>
                <PrimaryButton
                  type="button"
                  label="OK"
                  className="w-[120px] h-[45px] rounded-[5px] font-poppins font-[500] bg-[#00ADEF] text-white"
                  onClick={() => {
                    setEmailSent(false);
                    formik.resetForm();
                    setLocationDetails(null);
                    setCustomReasonId(null);
                    setCategoryId(null);
                    setSubCategoryIds([]);
                    setSubCategories([]);
                    setIsOtherCategorySelected(false);
                    setCustomCategory("");
                    setEmailExistsAsUser(false);
                    setMapKey(prev => prev + 1);
                    navigate("/");
                    window.scrollTo(0, 0);
                  }}
                />
              </div>
            }
          />
        )}
        <div className="w-full flex flex-col items-start sm:items-center px-2 sm:px-0">
          <p className="font-poppins font-[600] text-[20px] leading-[28px] sm:text-[18px] sm:font-[500] md:text-[24px] lg:text-[32px] text-[#303030] text-left sm:text-center">
            Get Started with <span className="text-[#00ADEF]">Swoodle</span>
          </p>
          <p className="font-poppins font-[400] text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px] text-[#767676] leading-[18px] sm:leading-[20px] md:leading-[24px] lg:leading-[27px] mt-2 mb-5 text-left sm:text-center">
            Enter your details below and start your journey
          </p>
        </div>
        <div className="flex flex-col items-center w-full max-w-[640px] sm:w-[70%] md:w-[60%] lg:w-[50%] xl:w-[45%] 2xl:w-[40%] getStarted px-2 sm:px-0">
          <Input
            type="text"
            label=""
            placeholder="Fundraiser Name"
            width="100%"
            radius="5px"
            InputDivClass="w-full mt-4"
            name="fundraiserName"
            data-field="fundraiserName"
            value={fundraiserName}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.fundraiserName}
            touched={touched.fundraiserName}
            inputClass="placeholder:!text-[#767676]"
          />
          <div className="w-full mt-4">
            <style>{`
              .fundraiser-map-container .gm-fullscreen-control {
                top: 53px !important;
              }
              .fundraiser-map-container .hidden.sm\\:block {
                display: block !important;
              }
              .fundraiser-map-container .sm\\:hidden {
                display: none !important;
              }
            `}</style>
            <div className="fundraiser-map-container">
              <GoogleMaps
                key={mapKey}
                onLocationSelect={handleLocationSelect}
                searchContainerClass="!w-[98%] !left-[1%] md:!w-[96%]"
                placeholder="Add fundraiser address"
                searchBarDivClassName="w-full flex items-center h-[50px] relative"
                searchBarInputClassName="navSearchBar h-full w-[100%] ps-[3%] pe-[12%] text-[14px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-[5px] border border-[#D9D9D9] placeholder:!text-[#767676]"
                searchIconClassName="absolute right-[3%] w-[20px] h-[20px]"
              />
            </div>
            {errors.fundraiserAddress && touched.fundraiserAddress && (
              <div className="ms-1 text-start w-full mt-1">
                <span className="text-red-600 text-[10px] sm:text-[10px] md:text-[12px] font-[600]">
                  {errors.fundraiserAddress}
                </span>
              </div>
            )}
          </div>
          <div className="flex mt-6 sm:mt-7 items-center gap-4 sm:gap-8 justify-start w-full" data-field="logo">
            <div
              className="relative h-[80px] w-[80px] rounded-[10px] border-2 border-solid border-[#E4E3E4] flex justify-center items-center logo cursor-pointer overflow-hidden bg-white"
              onClick={() => document.getElementById("fundraiserFileInput")?.click()}
            >
              {logoLoading === true && <Loader />}
              {logoFileImage !== undefined ? (
                <img
                  src={logoFileImage}
                  className="select-none"
                  style={{
                    imageRendering: "auto",
                    borderRadius: "10px",
                    display: "block",
                    ...logoPreviewStyle,
                  }}
                  onLoad={(event) => {
                    try {
                      const { naturalWidth, naturalHeight } = event.target || {};
                      if (!naturalWidth || !naturalHeight) {
                        setLogoPreviewStyle({});
                        return;
                      }
                      const widthRatio = naturalWidth / naturalHeight;
                      const heightRatio = naturalHeight / naturalWidth;
                      const widthMultiplier = 1.15;
                      const heightMultiplier = 1.08;
                      setLogoPreviewStyle({
                        width:
                          widthRatio >= 1
                            ? `${(widthRatio || 1) * 100 * widthMultiplier}%`
                            : `${100 * widthMultiplier}%`,
                        height:
                          heightRatio >= 1
                            ? `${(heightRatio || 1) * 100 * heightMultiplier}%`
                            : `${100 * heightMultiplier}%`,
                      });
                    } catch (error) {
                      setLogoPreviewStyle({});
                    }
                  }}
                  alt="Fundraiser Logo Preview"
                />
              ) : (
                <CameraIcon />)
              }
            </div>
            <input
              type="file"
              id="fundraiserFileInput"
              hidden
              accept=".jpg,.jpeg,.png,.svg"
              onChange={handleUploadLogo}
              onClick={(event) => { event.target.value = null; }}
            />
            {logoFileImage !== undefined ? (
              <p
                className="font-poppins font-[400] text-[12px] md:text-[14px] text-[#535353] cursor-pointer transition-colors hover:text-red-500"
                onClick={handleClearLogo}
              >
                Clear
              </p>
            ) : (
              <div className="flex flex-col justify-start">
                <p className="font-poppins font-[400] text-[12px] md:text-[14px] text-[#535353]">
                  Upload Fundraiser Logo <span className="text-[10px] md:text-[12px]">(Optional)</span>
                </p>
                <p className="font-poppins font-[400] text-[10px] md:text-[12px] text-[#00adef]">
                  File type: must be an svg, jpeg, jpg, or png
                </p>
                {/* Removed size note per request */}
              </div>
            )}
          </div>
          <Input
            type="text"
            label=""
            placeholder="Fundraiser Website URL"
            width="100%"
            radius="5px"
            InputDivClass="w-full mt-4"
            name="websiteUrl"
            data-field="websiteUrl"
            value={websiteUrl}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.websiteUrl}
            touched={touched.websiteUrl}
            inputClass="placeholder:!text-[#767676]"
          />
          <style>{`
          /* Standardize error text sizes for all inputs in getStarted form */
          .getStarted .text-red-600 {
            font-size: 10px !important;
          }
          @media (min-width: 640px) {
            .getStarted .text-red-600 {
              font-size: 10px !important;
            }
          }
          @media (min-width: 768px) {
            .getStarted .text-red-600 {
              font-size: 12px !important;
            }
          }
          /* Force placeholder color for Phone Input to match others (#767676) */
          .getStarted .react-tel-input input::placeholder {
            color: #767676 !important;
            opacity: 1 !important;
          }
        `}</style>
          <Input
            type="text"
            label=""
            placeholder="Fundraiser Contact Person Name"
            width="100%"
            radius="5px"
            InputDivClass="w-full mt-4"
            name="personName"
            data-field="personName"
            value={personName}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.personName}
            touched={touched.personName}
            inputClass="placeholder:!text-[#767676]"
          />
          <PhoneFormInput
            label={""}
            divClassName="w-full mt-4"
            name="personContact"
            data-field="personContact"
            value={personContact}
            handleBlur={handleBlur}
            error={errors.personContact}
            touched={touched.personContact}
            setFieldValue={setFieldValue}
            placeholder={"Contact Number"}
            inputStyle={{
              width: "100%",
              border: "none",
            }}
            buttonStyle={{ border: "none" }}
            buttonClass="!rounded-l-[5px] !bg-[#D9D9D9] md:!w-[13%] sm:!w-[15%] lg:!w-[9%] xl:!w-[7%] 2xl:!w-[6%]"
            containerClass="!rounded-[5px]"
            inputClass="lg:!h-[45px] md:!h-[45px] !rounded-[5px] !bg-[#FFFFFF] !text-[#303030] lg:!text-[16px] sm:text-[14px] !font-[400] !outline-none !font-poppins placeholder:!text-[#767676] placeholder:!opacity-100 placeholder:!font-poppins placeholder:!text-[14px]"
            labelClass="mb-5"
            errorClass={
              "text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600] ms-1"
            }
          />
          <Input
            type="text"
            label=""
            placeholder="Fundraiser Email"
            width="100%"
            radius="5px"
            InputDivClass="w-full mt-4"
            name="email"
            data-field="email"
            value={email}
            handleChange={(e) => {
              handleChange(e);
              // Clear error when user types
              if (emailExistsAsUser) {
                setEmailExistsAsUser(false);
              }
            }}
            handleBlur={async (e) => {
              handleBlur(e);
              // Check if email is registered as User/Member on blur
              if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                await checkEmailExistsAsUser(email);
              }
            }}
            error={emailExistsAsUser ? "This email is already registered. Please use a different email address or sign in with your existing account." : errors.email}
            touched={touched.email || emailExistsAsUser}
            inputClass="placeholder:!text-[#767676]"
          />
          {isCheckingEmail && (
            <p className="text-[10px] sm:text-[12px] text-[#767676] mt-1 w-full text-left">
              Checking email...
            </p>
          )}

          {/* Updated Category Selector - Single Selection */}
          <Selector
            value={fundraiserCategory}
            label={""}
            labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
            divClassName={`mt-4 ${fundraiserCategory === 'Community Organisations' ? 'mb-4' : 'mb-2'} w-full`}
            selectClassName="w-full rounded-[5px] bg-[#FFFFFF] text-[#303030] lg:text-[16px] sm:text-[12px] font-[400] outline-none !font-poppins !focus:outline-none"
            flexClassName="gap-1"
            options={fundraiserCategoryOptions}
            suffixIcon={<DownArrow />}
            placeholder={<span className="text-[#767676]">Select Fundraiser Category</span>}
            onChange={handleCategoryChange}
            handleBlur={handleBlur}
            error={errors.fundraiserCategory}
            touched={touched.fundraiserCategory}
            multiple={false}
            data-field="fundraiserCategory"
            errorClassName="mt-3 block"
          />
          {isOtherCategorySelected && (
            <div className="relative w-full mt-2">
              <InputTextArea
                label={""}
                placeholder={"Write custom category..."}
                divClassName="w-full"
                labelClassName="invitationLabels"
                textareaClassName="w-full h-[100px] px-4 pt-3 rounded-[10px] bg-[#F2F2F2] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins pr-24 pb-12 placeholder:text-[10px] sm:placeholder:text-[12px] lg:placeholder:text-[14px]"
                name="customCategory"
                data-field="customCategory"
                value={customCategory}
                handleBlur={() => { }}
                error={errors.fundraiserCategory}
                touched={touched.fundraiserCategory}
                setFieldValue={(field, val) => setCustomCategory(val)}
                showCounter={false}
              />
              <div className="absolute bottom-4 right-4">
                <PrimaryButton
                  type="button"
                  className="font-poppins text-[13px] sm:text-[12px] md:text-[13px] lg:text-[14px] text-center font-[500] h-[32px] sm:h-[32px] md:h-[36px] lg:h-[38px] rounded-[7px] px-3"
                  label="Submit"
                  disabled={!customCategory || customCategory.trim() === ""}
                  onClick={() => {
                    if (!customCategory || customCategory.trim() === "") return;
                    setFieldValue("fundraiserCategory", "Other");
                    toast.success("Custom category ready. Finish signup to save.");
                    setHideSubForCustom(true);
                  }}
                />
              </div>
            </div>
          )}
          {fundraiserCategory !== 'Community Organisations' && fundraiserCategory !== 'Other' && !hideSubForCustom && (
            <Selector
              value={fundraiserSubCategory}
              label={""}
              labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
              divClassName="mt-4 w-full multipleField"
              selectClassName="w-full rounded-[5px] bg-[#FFFFFF] text-[#303030] lg:text-[16px] sm:text-[10px] font-[400] outline-none !font-poppins !focus:outline-none"
              flexClassName="gap-1"
              options={subCategoryOptions}
              suffixIcon={<DownArrow />}
              placeholder={<span className="text-[#767676] -ml-3">Select Fundraiser SubCategory</span>}
              onChange={(values) => setFieldValue("fundraiserSubCategory", values)}
              handleBlur={handleBlur}
              error={errors.fundraiserSubCategory}
              touched={touched.fundraiserSubCategory}
              multiple={true}
              onDeselect={handleSubCategoryDeselect}
              disabled={!fundraiserCategory}
              data-field="fundraiserSubCategory"
              inputReadOnly={true}
              showSearch={false}
            />
          )}
          <InputTextArea
            label={"Tell us more about your Fundraiser"}
            placeholder={"Please share with us why people should support your cause"}
            divClassName="relative mt-4 w-full"
            labelClassName="invitationLabels !mb-2"
            textareaClassName="w-full h-[150px] px-3 pt-2 rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins placeholder:text-[12px] sm:placeholder:text-[13px] lg:placeholder:text-[15px]"
            name="aboutOrganization"
            data-field="aboutOrganization"
            value={aboutOrganization}
            handleBlur={handleBlur}
            error={errors.aboutOrganization}
            touched={touched.aboutOrganization}
            setFieldValue={setFieldValue}
          />
          {/* Updated Reasons Selector - Multi-select with custom tags */}
          <Selector
            value={Array.isArray(reasons) ? reasons : reasons ? [reasons] : []}
            label={"Choose your Fundraising cause"}
            labelClassName="lg:text-[14px] sm:text-[12px] font-[400] text-[#303030] mb-2 font-poppins lg:leading-[27px] sm:leading-[21px]"
            divClassName="mt-4 w-full multipleField"
            selectClassName="w-full rounded-[5px] bg-[#FFFFFF] text-[#303030] lg:text-[16px] sm:text-[12px] font-[400] outline-none !font-poppins !focus:outline-none cursor-pointer"
            flexClassName="gap-1"
            options={(
              (() => {
                const whitelist = [
                  // Removed "Taxes" per request; keep 5 primary reasons
                  "Volunteer fatigue",
                  "Fear of declining participants / attendees",
                  "Resources limitations",
                  "Lack of funds",
                  "Financial strain",
                ];
                const selectedList = Array.isArray(reasons) ? reasons : reasons ? [reasons] : [];
                const allowed = whitelist
                  .map((name) => ({ label: name, value: name }))
                  .filter((opt) => !selectedList.includes(opt.value));
                return [...allowed, { label: "Other", value: "Other" }];
              })()
            )}
            suffixIcon={<span className="cursor-pointer"><DownArrow /></span>}
            placeholder={<span className="text-[#767676] -ml-3">Select or add reasons</span>}
            onChange={(value) => {
              setFieldValue("reasons", value);
            }}
            handleBlur={handleBlur}
            error={errors.reasons}
            touched={touched.reasons}
            multiple={true}
            mode="tags"
            data-field="reasons"
          />
          {reasons.includes("Other") && (
            <div className="relative w-full mt-4">
              <InputTextArea
                label={""}
                placeholder={"Write other reason..."}
                divClassName="w-full"
                labelClassName="invitationLabels"
                textareaClassName="w-full h-[100px] px-4 pt-3 rounded-[10px] bg-[#F2F2F2] text-[#303030] lg:text-[14px] sm:text-[12px] font-[400] outline-none resize-none font-poppins pr-24 pb-12 placeholder:text-[10px] sm:placeholder:text-[12px] lg:placeholder:text-[14px]"
                name="newReason"
                data-field="newReason"
                value={newReason}
                handleBlur={handleBlur}
                error={errors.newReason}
                touched={touched.newReason}
                setFieldValue={setFieldValue}
                showCounter={false}
              />
              <div className="absolute bottom-4 right-4">
                <PrimaryButton
                  type="button"
                  className="font-poppins text-[13px] sm:text-[12px] md:text-[13px] lg:text-[14px] text-center font-[500] h-[32px] sm:h-[32px] md:h-[36px] lg:h-[38px] rounded-[7px] px-3"
                  label="Submit"
                  disabled={
                    !newReason ||
                    newReason.trim() === "" ||
                    reasons.includes(newReason.trim())
                  }
                  onClick={async () => {
                    if (
                      newReason &&
                      newReason.trim() !== "" &&
                      !reasons.includes(newReason.trim())
                    ) {
                      try {
                        // Call the API to create the reason (for backend record)
                        const createResponse = await dispatch(createReason({ reason: newReason.trim() }))?.unwrap();

                        if (createResponse?.success === true) {
                          // Store the created reason ID
                          setCustomReasonId(createResponse?.data?._id);

                          // Remove "Other" and add the trimmed new reason (title)
                          const updated = [
                            ...reasons.filter((r) => r !== "Other" && r.trim() !== ""),
                            newReason.trim()
                          ];
                          setFieldValue("reasons", updated);
                          setFieldValue("newReason", "");
                        }
                      } catch (error) {
                        toast.error("Failed to create custom reason. Please try again.");
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col items-center w-[92%] sm:w-[90%] mt-8 text-center px-1">
            <p className="text-[10px] sm:text-[12px] md:text-[14px] font-poppins font-[400]">
              By clicking sign up, I agree to the
              <span
                className="text-[#00ADEF] font-[500] cursor-pointer"
                onClick={() => window.open("https://swoodle.com.au/terms-and-conditions/", "_blank")}
              >
                {" "}
                Terms of Use{" "}
              </span>
              and that I have read the
              <span
                className="text-[#00ADEF] font-[500] cursor-pointer"
                onClick={() => window.open("https://swoodle.com.au/privacy-policy/", "_blank")}
              >
                {" "}
                Privacy Policy.
              </span>
            </p>

            <div className="flex items-center gap-1 mt-4">
              <p className="sm:text-[12px] md:text-[14px] font-poppins font-[400]">
                Already have an account?
              </p>
              <span
                className="text-[#00ADEF] font-[500] sm:text-[12px] md:text-[14px] cursor-pointer"
                onClick={() => {
                  navigate("/");
                  window.scrollTo(0, 0);
                }}
              >
                Sign In
              </span>
            </div>
          </div>

          <PrimaryButton
            type={"submit"}
            className={
              "w-[80%] sm:w-[60%] md:w-[60%] lg:w-[70%] font-poppins text-[16px] sm:text-[16px] md:text-[18px] lg:text-[20px] text-center font-[500] h-[46px] sm:h-[50px] md:h-[55px] lg:h-[60px] rounded-[10px] my-8 sm:my-10"
            }
            label="SIGN UP"
            disabled={logoLoading}
          />
        </div>
      </form>
    </>
  );
};

export default GettingStarted;